import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbFileText, TbFolder } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { type DriveFileListPayload, parseDriveFileListPayload } from "./driveFileListSchema";
import { DriveIcon } from "./driveIcon";

interface DriveFileListRendererProps {
  block: StreamingBlock;
}

const safeDriveUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

export const DriveFileListRenderer = (props: DriveFileListRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<DriveFileListPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseDriveFileListPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("drive_file_list_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().files.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <DriveIcon class="size-4 shrink-0" />
                <span class="truncate text-sm font-medium text-primary-text">{rt.t("drive_file_list_card.title")}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().files.length === 1
                    ? rt.t("drive_file_list_card.results_one")
                    : rt.t("drive_file_list_card.results", { count: String(data().files.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().files}>
                  {(file) => (
                    <li class="flex items-center gap-2 px-3 py-2">
                      <Show
                        when={file.isFolder}
                        fallback={<TbFileText class="size-4 shrink-0 text-muted-text" aria-hidden="true" />}
                      >
                        <TbFolder class="size-4 shrink-0 text-muted-text" aria-hidden="true" />
                      </Show>
                      <Show
                        when={safeDriveUrl(file.webViewLink)}
                        fallback={
                          <span class="truncate text-sm text-primary-text">
                            {file.name || rt.t("drive_file_list_card.untitled")}
                          </span>
                        }
                      >
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="truncate text-sm text-brand hover:underline"
                          >
                            {file.name || rt.t("drive_file_list_card.untitled")}
                          </a>
                        )}
                      </Show>
                      <Show when={formatIsoTimestamp(file.modifiedTime)}>
                        {(modified) => <span class="ml-auto shrink-0 text-xs text-muted-text">{modified()}</span>}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
